import { useMemo, useState } from "react";
import { motion } from "motion/react";
import type { Resource } from "../../api.ts";
import { fmtDateTime, fmtRelative } from "../../format.ts";
import { Button, EmptyState, Icon, LoadError, Loading, cn } from "../../ui/index.tsx";
import { EASE } from "../member/kit.tsx";
import { SOURCE_ICON, buildTimeline, type SourceKind } from "./model.ts";
import type { Entitlement, Game, Redemption } from "../../../shared/types.ts";

const TINT: Record<SourceKind, string> = {
  key: "border-accent/30 bg-accent-soft text-accent",
  purchase: "border-ok/30 bg-ok/10 text-ok",
  staff: "border-info/30 bg-info/10 text-info",
  migration: "border-line-strong bg-raised text-muted",
  other: "border-warn/30 bg-warn/10 text-warn",
};

const PAGE = 6;

export function History({ redemptions, entitlements, games }: { redemptions: Resource<Redemption[]>; entitlements: Entitlement[]; games: Map<string, Game> }) {
  const [all, setAll] = useState(false);
  const items = useMemo(
    () => buildTimeline(redemptions.data ?? [], entitlements, (id) => games.get(id)?.name ?? id),
    [redemptions.data, entitlements, games],
  );
  const shown = all ? items : items.slice(0, PAGE);

  return (
    <section aria-label="History" className="pane">
      <header className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
        <h2 className="font-display text-[15px] font-semibold uppercase tracking-wide">History</h2>
        {items.length > 0 && <span className="kicker">{items.length} events</span>}
      </header>
      {items.length === 0 ? (
        redemptions.loading && !redemptions.data ? <Loading /> : redemptions.error && !redemptions.data ? <LoadError error={redemptions.error} retry={redemptions.reload} /> :
          <EmptyState icon="clock" title="Nothing here yet" body="Keys you redeem and licenses you're granted will show up as a timeline." />
      ) : (
        <>
          <ol className="relative p-5">
            <span className="absolute bottom-8 left-[2.15rem] top-8 w-px bg-line" aria-hidden />
            {shown.map((it, i) => (
              <motion.li key={it.id} initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: Math.min(i, PAGE) * 0.05, ease: EASE }} className="relative flex gap-3 pb-4 last:pb-0">
                <span className={cn("relative z-10 grid size-8 shrink-0 place-items-center rounded-full border", TINT[it.kind])}>
                  <Icon name={SOURCE_ICON[it.kind]} className="size-3.5" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="truncate text-sm font-medium">{it.title}</span>
                    <time className="shrink-0 text-xs text-faint" title={fmtDateTime(it.at)}>{fmtRelative(it.at)}</time>
                  </div>
                  <p className="truncate text-xs text-muted" title={it.detail}>{it.detail}</p>
                </div>
              </motion.li>
            ))}
          </ol>
          {items.length > PAGE && (
            <div className="border-t border-line px-5 py-3">
              <Button variant="ghost" size="sm" icon={all ? "chevronDown" : "plus"} onClick={() => setAll((v) => !v)}>{all ? "Show less" : `Show all ${items.length}`}</Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
